import { Paper, PaperProps } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { rgba } from 'polished';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

/**
 * Platform-wide learning-time grid — parity with the tail of the retired
 * all-classrooms Analytics page, but self-contained (direct call instead of
 * the admin redux slice) so the shared hub never imports admin-only modules.
 * Rendered on the admin portal only; the instructor endpoint set has no
 * unscoped time aggregate.
 */
const PlatformLearningTime: React.FC = () => {
	const { displaySnackbar } = useSnackbar();
	const [values, setValues] = useState<Record<string, number> | null>(null);

	useEffect(() => {
		api
			.post('classroom/analytics/time', {})
			.then((response) => setValues(response.data.data || {}))
			.catch((error: any) =>
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load learning time'
				)
			);
	}, [displaySnackbar]);

	const colors: Record<string, string> = {
		total: '#5AC0FC',
		videos: '#6C63FF',
		documents: '#FF8532',
		tests: '#FFD037',
		webinar: '#ff3100',
		webcontent: '#36a2eb',
		assignment: '#00ce67',
	};
	const labels: Record<string, string> = {
		total: 'learning time',
		videos: 'videos',
		documents: 'documents',
		tests: 'tests',
		webinar: 'webinars',
		webcontent: 'web content',
		assignment: 'assignment',
	};
	const time = (seconds: number) => {
		if (!seconds) return '0';
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);
		const days = Math.floor(hours / 24);
		if (days) return `${days}d ${hours % 24}h`;
		if (hours) return `${hours}h ${minutes % 60}m`;
		return `${minutes}m`;
	};

	if (!values) return null;
	return (
		<LearningCardsGrid>
			{Object.keys(colors).map((key) => (
				<LearningCard key={key} $color={colors[key]}>
					<LearningTypeValue color={colors[key]}>{labels[key]}</LearningTypeValue>
					<LearningTypeName>{time(Number(values[key] || 0))}</LearningTypeName>
				</LearningCard>
			))}
		</LearningCardsGrid>
	);
};

const LearningCardsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(10.5rem, 1fr));
	gap: 1rem;
	width: 100%;
	padding-bottom: 5rem;
	margin-top: 2rem;
`;

const LearningPaper = styled(Paper).attrs<PaperProps>({ elevation: 0 })`
	padding: 1.25rem;
`;

const LearningCard = styled(LearningPaper)<{ $color: string }>`
	display: flex;
	min-width: 0;
	flex-direction: column;
	justify-content: space-between;
	gap: 0.5rem;
	margin: 0;
	background: ${(props) => rgba(props.$color, 0.1)};
	color: ${(props) => props.$color};
	max-height: 7.75rem;
`;

const LearningTypeValue = styled.div<{ color?: string }>`
	color: ${(props) => props.color};
	font-size: 1.063rem;
	font-weight: 600;
	text-transform: capitalize;
`;

const LearningTypeName = styled.div`
	font-size: 1.063rem;
	font-weight: 600;
	color: #251b33;
	text-align: end;
`;

export default PlatformLearningTime;
